import React, { useState } from 'react';
import { Users, Check } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  CardErrorNotice,
  hashHue,
  type F8CardData,
  type OnAction,
} from './cardHelpers';
import { joinCircle } from '../../../services/circleService';
import { slugify } from '../../../utils/circleSlug';
// PPL-508 — the circles gate: one field, one way out.
import {
  firstNameGateMessage,
  FIRST_NAME_ACTION_LABEL,
  PROFILE_EDIT_PATH,
} from '../../../utils/circleGate';

interface Circle {
  circleId?: string;
  id?: string;
  name?: string;
  description?: string;
  memberCount?: number;
  max?: number;
  tags?: string[];
  isMember?: boolean;
  joined?: boolean;
}

interface ExploreMoreAction {
  label?: string;
  action: 'navigate' | 'cloud_function' | 'clipboard' | 'skill_invoke';
  params?: Record<string, any>;
}

function circleInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function CircleRow({ circle, onAction }: { circle: Circle; onAction?: OnAction }) {
  const navigate = useNavigate();
  const id = circle.circleId || circle.id;
  const name = circle.name || 'Unnamed Circle';
  const desc = circle.description || '';
  const members = circle.memberCount || 0;
  const max = circle.max || 50;
  const tags = Array.isArray(circle.tags) ? circle.tags : [];
  const initiallyJoined = !!(circle.isMember || circle.joined);
  const hue = hashHue(name);

  const [joined, setJoined] = useState(initiallyJoined);
  const [joining, setJoining] = useState(false);
  // PPL-508 — a refused join used to reach console.warn and nowhere else, so
  // the row looked untouched. The reason (and the way out) now land under it.
  const [joinError, setJoinError] = useState<{ text: string; gate: boolean } | null>(null);

  const thumbStyle: React.CSSProperties = {
    background: `linear-gradient(135deg,
      oklch(0.55 0.22 ${hue}) 0%,
      oklch(0.65 0.25 ${(hue + 60) % 360}) 100%)`,
  };

  const handleView = () => {
    if (!id) return;
    navigate(`/circles/${slugify(name)}`, { state: { circleId: id } });
  };

  const handleJoin = async () => {
    if (!id || joining || joined) return;
    setJoining(true);
    setJoinError(null);
    try {
      await joinCircle(id);
      setJoined(true);
    } catch (err: any) {
      setJoinError({
        text: firstNameGateMessage(err) || err?.message || 'could not join this circle',
        gate: firstNameGateMessage(err) !== null,
      });
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="f8c-circle-row">
      <div className="f8c-circle-thumb" style={thumbStyle}>
        <span className="f8c-circle-thumb-initials">{circleInitials(name)}</span>
        <span className="f8c-circle-thumb-shine" />
      </div>

      <div className="f8c-circle-body">
        <div className="f8c-circle-name">{name}</div>
        {desc && <div className="f8c-circle-desc">{desc}</div>}
        <div className="f8c-circle-meta">
          <span className="f8c-circle-members">
            <Users size={11} />
            <span>{members}/{max}</span>
          </span>
          {tags.slice(0, 1).map((tag) => (
            <span key={tag} className="f8c-circle-tag">
              {tag}
            </span>
          ))}
        </div>
        <CardErrorNotice
          text={joinError?.text}
          actionLabel={joinError?.gate ? FIRST_NAME_ACTION_LABEL : null}
          actionTo={joinError?.gate ? PROFILE_EDIT_PATH : null}
        />
      </div>

      <div className="f8c-circle-actions">
        <CardButton small onClick={handleView}>
          View
        </CardButton>
        {joined ? (
          <span className="f8c-circle-joined">
            <Check size={11} /> Joined
          </span>
        ) : (
          <CardButton primary small onClick={handleJoin} disabled={joining}>
            {joining ? 'Joining…' : 'Join'}
          </CardButton>
        )}
      </div>
    </div>
  );
}

export default function CirclesListCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const circles: Circle[] = card?.data?.circles || [];
  const exploreMore: ExploreMoreAction | undefined = card?.data?.exploreMore;
  if (circles.length === 0) return null;

  return (
    <InlineShell>
      <InlineEyebrow label={`${circles.length} circles`} />

      <div className="f8c-circle-list">
        {circles.map((circle, i) => (
          <CircleRow
            key={circle.circleId || circle.id || i}
            circle={circle}
            onAction={onAction}
          />
        ))}
      </div>

      {exploreMore && (
        <div className="f8c-circle-more">
          <CardButton
            small
            onClick={() =>
              onAction?.({
                action: exploreMore.action,
                params: exploreMore.params,
              })
            }
          >
            {exploreMore.label || 'Explore more circles'} →
          </CardButton>
        </div>
      )}
    </InlineShell>
  );
}
